import React, { JSX, useEffect, useRef, useState, useCallback } from "react";
import { Container, Indicator, Item } from "./FloatIndicator.styled";
import { SxProps, Theme } from "@mui/material";
import { FRAME_BORDER_WIDTH } from "../../constants";

export type FloatIndicatorProps = {
  onIndexChange?: (newIndex: number) => void;
  range?: number;
  activeIndex?: number;
  floatingWidth?: number;
  floatingHeight?: number | any;
  isValid?: boolean;
  indicatorSxProps?: SxProps<Theme>;
} & React.ComponentProps<typeof Container>;

const TIMEOUT_MOVEMENT = 50;
const TIMEOUT_VISIBILITY = TIMEOUT_MOVEMENT + 100;
const RESIZE_DEBOUNCE = 50; // ms

export const FloatIndicator: React.FC<FloatIndicatorProps> = (props): JSX.Element => {
  const {
    onIndexChange,
    range = 1,
    activeIndex = 0,
    floatingWidth = 20,
    floatingHeight = FRAME_BORDER_WIDTH,
    isValid = true,
    indicatorSxProps = {},
    children,
    ...rest
  } = props;

  // Internal index, driven by activeIndex, but can be changed by user clicks
  const [indexState, setIndexState] = useState<FloatIndicatorProps["activeIndex"]>(activeIndex);
  const [offset, setOffset] = useState<number>(0);
  const [visible, setVisible] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const resizeTimeoutRef = useRef<number | null>(null);

  // Sync internal state when parent changes activeIndex
  useEffect(() => {
    setIndexState(activeIndex);
  }, [activeIndex]);

  // Calculate indicator offset based on current indexState
  const moveIndicator = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const childrenNodes = container.children;
    const items = Array.from(childrenNodes).slice(
      0,
      childrenNodes.length - 1
    ) as HTMLElement[];

    const idx = typeof indexState === "number" ? indexState : 0;
    const activeEl = items[idx];
    if (!activeEl) return;

    const containerRect = container.getBoundingClientRect();
    const rect = activeEl.getBoundingClientRect();

    const centerX = rect.left - containerRect.left + rect.width / 2;

    setOffset(centerX);
  }, [indexState]);

  // Recalculate position when index changes
  useEffect(() => {
    const id = window.setTimeout(moveIndicator, TIMEOUT_MOVEMENT);
    return () => window.clearTimeout(id);
  }, [moveIndicator]);

  // Recalculate on window resize (with debounce)
  useEffect(() => {
    if (!isValid) return;

    const handleResize = () => {
      if (resizeTimeoutRef.current != null) {
        window.clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = window.setTimeout(() => {
        moveIndicator();
      }, RESIZE_DEBOUNCE);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (resizeTimeoutRef.current != null) {
        window.clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [moveIndicator, isValid]);

  // Show indicator with fade-in
  useEffect(() => {
    const id = window.setTimeout(() => setVisible(true), TIMEOUT_VISIBILITY);
    return () => window.clearTimeout(id);
  }, []);

  // Handle click: update local state AND notify parent
  const handleItemClick = (index: number) => {
    setIndexState(index);
    onIndexChange?.(index);
  };

  return (
    <Container {...rest} ref={containerRef}>
      {React.Children.map(children, (child, index) =>
        React.isValidElement(child) ? (
          <Item key={child.key}>
            {React.cloneElement(child, {
              // keep user's onClick, if any
              // @ts-ignore
              onClick: (e: any) => {
                // @ts-ignore - call child's own onClick first
                if (typeof child.props.onClick === "function") {
                  /* @ts-ignore */
                  child.props.onClick(e);
                }
                handleItemClick(index);
              },
            })}
          </Item>
        ) : (
          child
        )
      )}

      {isValid && (
        <Indicator
          className={'indicator'}
          sx={{
            width: floatingWidth,
            height: Object.entries(floatingHeight).reduce((acc, [k, v]) => {
              /* @ts-ignore */
              acc[k] = `${v}px`;
              return acc;
            }, {}),
            left: offset,
            opacity: visible ? 1 : 0,
            transition: visible ? "opacity .35s ease, left .35s ease" : "none",
            ...indicatorSxProps,
          }}
        />
      )}
    </Container>
  );
};
